import { AssetError } from '@dg-embalajes/assets';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useFontReadiness } from '@/hooks/useFontReadiness';
import { usePortSnapshot } from '@/hooks/usePortSnapshot';
import type { AssetInspection, AssetRole } from '@/ports/editor';
import type { EditorRuntimePorts } from '@/ports/runtime';
import { normalizeImportedFile } from '@/runtime/normalizeImportedFile';
import { AccountDialog } from './dialogs/AccountDialog';
import { AppMenu } from './dialogs/AppMenu';
import { AssetImportDialog } from './dialogs/AssetImportDialog';
import { ProductSettingsDialog } from './dialogs/ProductSettingsDialog';
import { ProjectsDialog } from './dialogs/ProjectsDialog';
import { PwaDialog } from './dialogs/PwaDialog';
import { ContextPanel } from './editor/ContextPanel';
import { EditorSurface } from './editor/EditorSurface';
import { NoticeBar } from './editor/NoticeBar';
import { ToolDock, type DockTool } from './editor/ToolDock';
import { TopBar } from './editor/TopBar';
import { AddPanel } from './panels/AddPanel';
import { BackgroundPanel } from './panels/BackgroundPanel';
import { ElementPanel } from './panels/ElementPanel';
import { FinishPanel } from './panels/FinishPanel';
import { LayersPanel } from './panels/LayersPanel';
import { TablePanel } from './panels/TablePanel';

type PanelId = DockTool | null;
type DialogId = 'menu' | 'account' | 'projects' | 'pwa' | 'product' | null;

interface ImportState {
  readonly role: AssetRole;
  readonly replace: boolean;
  readonly loading: boolean;
  readonly inspection: AssetInspection | null;
  readonly error: string | null;
}

export function friendlyAssetImportError(error: unknown): string {
  if (!(error instanceof AssetError)) return 'No pudimos abrir el archivo. Probá de nuevo o elegí otro.';
  switch (error.code) {
    case 'ASSET_EMPTY':
      return 'El archivo está vacío. Elegí otro archivo.';
    case 'ASSET_TOO_LARGE':
    case 'ASSET_PIXEL_LIMIT':
      return 'La imagen es demasiado grande para abrirla. Probá con una versión más liviana.';
    case 'ASSET_FORMAT_UNSUPPORTED':
    case 'ASSET_MIME_MISMATCH':
      return 'Ese tipo de archivo no sirve como imagen. Elegí una foto, una imagen o un PDF.';
    case 'ASSET_HEIC_UNSUPPORTED':
      return 'No pudimos preparar esta foto en este dispositivo. Probá de nuevo o elegí otra.';
    case 'ASSET_SVG_UNSAFE':
      return 'Ese SVG no se puede usar de forma segura. Exportalo como PNG o PDF y volvé a intentarlo.';
    case 'ASSET_PDF_PAGE_LIMIT':
    case 'ASSET_PDF_PAGE_OUT_OF_RANGE':
    case 'ASSET_PDF_PREVIEW_FAILED':
      return 'No pudimos preparar ese PDF. Guardalo de nuevo o probá con un PDF más corto.';
    case 'ASSET_CORRUPT':
    case 'ASSET_DIMENSIONS_INVALID':
    case 'ASSET_PDF_ENCRYPTED_OR_INVALID':
      return 'El archivo parece dañado o no se puede leer. Abrilo, guardalo de nuevo y volvé a intentarlo.';
    default:
      return 'No pudimos abrir el archivo en este dispositivo. Probá de nuevo o elegí otro.';
  }
}

export function EditorScreen({ runtime }: { readonly runtime: EditorRuntimePorts }) {
  const editorSnapshot = usePortSnapshot(runtime.editor);
  const accountSnapshot = usePortSnapshot(runtime.account);
  const projectsSnapshot = usePortSnapshot(runtime.projects);
  const pwaSnapshot = usePortSnapshot(runtime.pwa);
  const fontReadiness = useFontReadiness(runtime.runtimeKind === 'integrated');
  const [activeDock, setActiveDock] = useState<DockTool>('table');
  const [panel, setPanel] = useState<PanelId>(null);
  const [panelCollapsed, setPanelCollapsed] = useState(false);
  const [dialog, setDialog] = useState<DialogId>(null);
  const [editingTextId, setEditingTextId] = useState<string | null>(null);
  const [pendingCloudSave, setPendingCloudSave] = useState(false);
  const [importState, setImportState] = useState<ImportState | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const allowBrowserExitRef = useRef(false);

  const finishEditingSession = useCallback(async () => {
    if (editingTextId) await runtime.editor.endTextEditing(editingTextId);
    setEditingTextId(null);
  }, [editingTextId, runtime.editor]);

  const selectDock = async (tool: DockTool) => {
    await finishEditingSession();
    setActiveDock(tool);
    setPanelCollapsed(false);
    setPanel((current) => current === tool ? null : tool);
    runtime.editor.setInteractionMode(tool === 'table' ? 'table' : 'element');
  };

  const handleSelection = useCallback((id: string | null) => {
    void (async () => {
      if (editingTextId && editingTextId !== id) await runtime.editor.endTextEditing(editingTextId);
      if (editingTextId !== id) setEditingTextId(null);
      runtime.editor.select(id);
      if (id) {
        runtime.editor.setInteractionMode('element');
        setActiveDock('element');
        setPanel('element');
        setPanelCollapsed(false);
      }
    })();
  }, [editingTextId, runtime.editor]);

  const handleLayerSelection = useCallback((id: string) => {
    void (async () => {
      if (editingTextId && editingTextId !== id) await runtime.editor.endTextEditing(editingTextId);
      if (editingTextId !== id) setEditingTextId(null);
      runtime.editor.select(id);
      runtime.editor.setInteractionMode('element');
      setActiveDock('layers');
      setPanel('layers');
      setPanelCollapsed(false);
    })();
  }, [editingTextId, runtime.editor]);

  const handleTextEdit = useCallback((id: string) => {
    void (async () => {
      if (editingTextId && editingTextId !== id) await runtime.editor.endTextEditing(editingTextId);
      runtime.editor.select(id);
      runtime.editor.setInteractionMode('element');
      setActiveDock('element');
      setEditingTextId(id);
      setPanel('element');
      setPanelCollapsed(false);
      runtime.editor.beginTextEditing(id, window.visualViewport?.height ?? window.innerHeight);
    })();
  }, [editingTextId, runtime.editor]);

  const openImport = async (role: AssetRole, replace = false) => {
    await finishEditingSession();
    setImportState({ role, replace, loading: false, inspection: null, error: null });
    window.setTimeout(() => fileInputRef.current?.click(), 0);
  };

  const inspectFile = useCallback(async (file: File, request: Pick<ImportState, 'role' | 'replace'>) => {
    const state: ImportState = { ...request, loading: true, error: null, inspection: null };
    setImportState({ ...state, loading: true, error: null, inspection: null });
    try {
      const normalized = await normalizeImportedFile(file);
      const role: AssetRole = request.replace || request.role === 'background'
        ? request.role
        : normalized.kind === 'pdf'
          ? 'pdf-art'
          : request.role === 'pdf-art'
            ? editorSnapshot.document?.product.kind === 'sticker' ? 'sticker-image' : 'logo'
            : request.role;
      const inspection = await runtime.editor.inspectAsset(normalized.file, role);
      setImportState({ ...state, role, loading: false, inspection, error: null });
    } catch (error) {
      setImportState({ ...state, loading: false, inspection: null, error: friendlyAssetImportError(error) });
    }
  }, [editorSnapshot.document?.product.kind, runtime.editor]);

  const handleFileDrop = useCallback((files: readonly File[]) => {
    void (async () => {
      if (dialog || importState || !editorSnapshot.document) return;
      await finishEditingSession();
      const file = files[0];
      const role: AssetRole = editorSnapshot.document.product.kind === 'sticker' ? 'sticker-image' : 'logo';
      if (files.length !== 1 || !file) {
        setImportState({
          role,
          replace: false,
          loading: false,
          inspection: null,
          error: 'Arrastrá un solo archivo por vez.',
        });
        return;
      }
      await inspectFile(file, { role, replace: false });
    })();
  }, [dialog, editorSnapshot.document, finishEditingSession, importState, inspectFile]);

  const confirmImport = async (page: number) => {
    if (!importState?.inspection) return;
    if (importState.replace) await runtime.editor.replaceSelectedAsset(importState.inspection, page);
    else await runtime.editor.addAsset(importState.inspection, importState.role, page);
    setPanel('element');
    setPanelCollapsed(false);
  };

  const saveCloud = async () => {
    await finishEditingSession();
    if (accountSnapshot.status !== 'authenticated') {
      setPendingCloudSave(true);
      setDialog('account');
      return;
    }
    const result = await runtime.projects.saveCurrent();
    setDialog(result.kind === 'saved' ? null : 'projects');
  };

  const finishLoginAction = async () => {
    if (pendingCloudSave) {
      const result = await runtime.projects.saveCurrent();
      setPendingCloudSave(false);
      setDialog(result.kind === 'saved' ? null : 'projects');
    }
  };

  const startNew = async () => {
    await finishEditingSession();
    if (editorSnapshot.saveStatus === 'saving') return;
    if (window.confirm('El borrador actual queda guardado en este dispositivo. ¿Comenzar un diseño nuevo?')) {
      try {
        await runtime.editor.closeProject();
        setDialog(null);
      } catch {
        // El runtime conserva abierto el proyecto y muestra el error persistente.
      }
    }
  };

  useEffect(() => {
    if (!window.history.state?.dgEditorGuard) {
      window.history.pushState({ ...window.history.state, dgEditorGuard: true }, '', window.location.href);
    }
  }, []);

  useEffect(() => {
    const deleteSelectedWithKeyboard = (event: KeyboardEvent) => {
      if (event.key !== 'Delete' && event.key !== 'Backspace') return;
      if (event.defaultPrevented || event.repeat || event.ctrlKey || event.metaKey || event.altKey) return;
      if (dialog || importState || editingTextId) return;

      const target = event.target;
      if (target instanceof HTMLElement) {
        if (target.isContentEditable || target.matches('textarea, [role="textbox"]')) return;
        if (target instanceof HTMLInputElement && ![
          'button', 'checkbox', 'color', 'file', 'hidden', 'image', 'radio', 'range', 'reset', 'submit',
        ].includes(target.type)) return;
      }

      const selected = editorSnapshot.layers.find((layer) => layer.id === editorSnapshot.selectedId);
      if (!selected?.canDelete || selected.locked) return;

      event.preventDefault();
      runtime.editor.delete(selected.id);
    };

    document.addEventListener('keydown', deleteSelectedWithKeyboard);
    return () => document.removeEventListener('keydown', deleteSelectedWithKeyboard);
  }, [dialog, editingTextId, editorSnapshot.layers, editorSnapshot.selectedId, importState, runtime.editor]);

  useEffect(() => {
    const isNativeTextTarget = (target: EventTarget | null): boolean => {
      if (!(target instanceof HTMLElement)) return false;
      if (target.isContentEditable || target.matches('textarea, [role="textbox"]')) return true;
      if (!(target instanceof HTMLInputElement)) return false;
      return !['button', 'checkbox', 'color', 'file', 'hidden', 'image', 'radio', 'range', 'reset', 'submit'].includes(target.type);
    };

    const handleEditorShortcut = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.repeat || event.altKey || !(event.ctrlKey || event.metaKey)) return;
      if (dialog || importState || editingTextId || isNativeTextTarget(event.target)) return;

      const key = event.key.toLowerCase();
      const selected = editorSnapshot.layers.find((layer) => layer.id === editorSnapshot.selectedId);
      if (key === 'z') {
        const redo = event.shiftKey;
        if (redo ? !editorSnapshot.canRedo : !editorSnapshot.canUndo) return;
        event.preventDefault();
        if (redo) runtime.editor.redo();
        else runtime.editor.undo();
        return;
      }
      if (key === 'y') {
        if (!editorSnapshot.canRedo) return;
        event.preventDefault();
        runtime.editor.redo();
        return;
      }
      if (key === 'c') {
        if (!selected?.canDuplicate) return;
        event.preventDefault();
        runtime.editor.copy(selected.id);
        return;
      }
      if (key === 'x') {
        if (!selected?.canDelete || !selected.canDuplicate || selected.locked) return;
        event.preventDefault();
        runtime.editor.cut(selected.id);
        return;
      }
      if (key === 'v') {
        event.preventDefault();
        runtime.editor.paste();
      }
    };

    document.addEventListener('keydown', handleEditorShortcut);
    return () => document.removeEventListener('keydown', handleEditorShortcut);
  }, [dialog, editingTextId, editorSnapshot.canRedo, editorSnapshot.canUndo, editorSnapshot.layers, editorSnapshot.selectedId, importState, runtime.editor]);

  useEffect(() => {
    const onBack = () => {
      if (allowBrowserExitRef.current) return;
      const hasTransientUi = Boolean(importState || dialog || editingTextId || panel);
      if (importState) setImportState(null);
      else if (dialog) {
        setDialog(null);
        setPendingCloudSave(false);
      } else if (editingTextId) void finishEditingSession();
      else if (panel) {
        void finishEditingSession();
        setPanel(null);
      }
      if (hasTransientUi) {
        window.history.pushState({ ...window.history.state, dgEditorGuard: true }, '', window.location.href);
      } else {
        allowBrowserExitRef.current = true;
        window.history.back();
      }
    };
    window.addEventListener('popstate', onBack);
    return () => window.removeEventListener('popstate', onBack);
  }, [dialog, editingTextId, finishEditingSession, importState, panel]);

  const renderPanel = () => {
    switch (panel) {
      case 'table':
        return <TablePanel editor={runtime.editor} snapshot={editorSnapshot} onReconfigure={() => { void finishEditingSession().then(() => setDialog('product')); }} />;
      case 'background':
        return <BackgroundPanel editor={runtime.editor} snapshot={editorSnapshot} onImport={(role, replace) => void openImport(role, replace)} />;
      case 'add':
        return <AddPanel editor={runtime.editor} snapshot={editorSnapshot} onImport={(role) => void openImport(role)} onTextAdded={(id) => { setPanel('element'); setEditingTextId(null); runtime.editor.select(id); }} />;
      case 'layers':
        return <LayersPanel editor={runtime.editor} snapshot={editorSnapshot} onSelect={handleLayerSelection} />;
      case 'finish':
        return <FinishPanel editor={runtime.editor} snapshot={editorSnapshot} account={accountSnapshot} fontsReady={fontReadiness.status === 'ready'} cloudSaveResult={projectsSnapshot.lastSaveResult} onSaveCloud={() => void saveCloud()} onOfferPwa={() => { void finishEditingSession().then(() => setDialog('pwa')); }} />;
      case 'element':
        return <ElementPanel editor={runtime.editor} snapshot={editorSnapshot} editingTextId={editingTextId} fonts={fontReadiness} onEditingTextChange={setEditingTextId} onReplaceAsset={(role) => void openImport(role, true)} />;
      default:
        return <div className="desktop-inspector-idle"><strong>Mesa lista</strong><p>Elegí una herramienta o tocá un elemento para ver sus propiedades.</p></div>;
    }
  };

  const panelTitle = panel === 'table' ? 'Mesa' : panel === 'background' ? 'Fondo' : panel === 'add' ? 'Agregar' : panel === 'layers' ? 'Capas' : panel === 'finish' ? 'Finalizar' : panel === 'element' ? 'Elemento y propiedades' : 'Inspector';

  return (
    <div className={`editor-shell ${panel ? 'has-panel' : ''} ${editingTextId ? 'is-editing-text' : ''}`}>
      <TopBar snapshot={editorSnapshot} onMenu={() => { void finishEditingSession().then(() => setDialog('menu')); }} onUndo={() => runtime.editor.undo()} onRedo={() => runtime.editor.redo()} />
      <div className="editor-layout">
        <ToolDock active={panel === 'element' ? 'element' : activeDock} onSelect={selectDock} />
        <EditorSurface port={runtime.editor} snapshot={editorSnapshot} onSelection={handleSelection} onTextEdit={handleTextEdit} onEmptyStickerImport={() => void openImport('sticker-image')} onFileDrop={handleFileDrop} />
        {panel ? (
          <ContextPanel title={panelTitle} eyebrow={panel === 'element' ? 'Objeto activo' : undefined} onClose={() => { void finishEditingSession().then(() => setPanel(null)); }} collapsed={panelCollapsed} onToggleCollapsed={() => setPanelCollapsed((value) => !value)}>
            {renderPanel()}
          </ContextPanel>
        ) : (
          <div className="desktop-inspector"><div className="desktop-inspector-idle"><strong>Mesa lista</strong><p>Elegí una herramienta o tocá un elemento para ver sus propiedades.</p></div></div>
        )}
      </div>
      {editorSnapshot.notice ? <NoticeBar notice={editorSnapshot.notice} onClose={() => runtime.editor.dismissNotice()} /> : null}

      <input
        ref={fileInputRef}
        className="visually-hidden-file"
        type="file"
        accept=".jpg,.jpeg,.png,.webp,.svg,.pdf,.heic,.heif,image/jpeg,image/png,image/webp,image/svg+xml,image/heic,image/heif,application/pdf"
        onChange={(event) => {
          const file = event.target.files?.[0];
          const request = importState;
          event.target.value = '';
          if (file && request) void inspectFile(file, request);
          else setImportState(null);
        }}
      />

      {importState && (importState.loading || importState.inspection || importState.error) ? (
        <AssetImportDialog role={importState.role} inspection={importState.inspection} loading={importState.loading} error={importState.error} onClose={() => setImportState(null)} onConfirm={confirmImport} onRetry={() => fileInputRef.current?.click()} />
      ) : null}
      {dialog === 'menu' ? <AppMenu editor={editorSnapshot} account={accountSnapshot} onClose={() => setDialog(null)} onProjects={() => setDialog('projects')} onCloudSave={() => void saveCloud()} onAccount={() => { setPendingCloudSave(false); setDialog('account'); }} onPwa={() => setDialog('pwa')} onNewProject={() => void startNew()} /> : null}
      {dialog === 'account' ? <AccountDialog account={runtime.account} snapshot={accountSnapshot} onClose={() => { setDialog(null); setPendingCloudSave(false); }} onAuthenticated={finishLoginAction} /> : null}
      {dialog === 'projects' ? <ProjectsDialog projects={runtime.projects} snapshot={projectsSnapshot} onClose={() => setDialog(null)} /> : null}
      {dialog === 'pwa' ? <PwaDialog pwa={runtime.pwa} snapshot={pwaSnapshot} onClose={() => setDialog(null)} /> : null}
      {dialog === 'product' ? <ProductSettingsDialog editor={runtime.editor} snapshot={editorSnapshot} onClose={() => setDialog(null)} /> : null}
    </div>
  );
}
